import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, MapPin } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { DEFAULT_LOCATIONS } from '@/lib/defaultLocations';
import { QuantitySelector } from '@/components/QuantitySelector';
import { Plus } from '@/components/icons';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { useVibrate } from '@/hooks/useVibrate';

const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatTimeToAMPM = (time: string): string => {
  const [hours, minutes] = time.split(':').slice(0, 2);
  const h = parseInt(hours, 10);
  const m = minutes;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const cleanHTMLTags = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/<[^>]*>/g, '') // Remover todas las etiquetas HTML
    .replace(/&nbsp;/g, ' ')  // Reemplazar espacios HTML
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim();
};

export interface Tour {
  id: string;
  title: string;
  description: string;
  fixed_sale_price: number;
  latitude: number;
  longitude: number;
  departure_time?: string;
  timezone?: string;
  main_image_url?: string;
  image_urls?: string[];
  details?: {
    origin?: { name: string };
    destination?: { name: string };
    duration_days?: number;
    capacity?: number;
    image_url?: string;
  };
  distance_km?: number;
  store_id?: string;
  store_name?: string;
  is_digital?: boolean;
}

interface StoreRow {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  status?: string | null;
}

interface StoreAddressRow {
  store_id: string;
  latitude: number | null;
  longitude: number | null;
  is_primary?: boolean | null;
}

interface ProductTourRow {
  id: string;
  title: string;
  description: string;
  fixed_sale_price: number;
  latitude: number;
  longitude: number;
  departure_time?: string;
  timezone?: string;
  main_image_url?: string;
  image_urls?: string[];
  details?: Tour['details'];
  store_id?: string;
  is_digital?: boolean;
}

interface ToursCardListProps {
  userLocation: { latitude: number; longitude: number };
  radius?: number;
  selectedCityName?: string;
  selectedTourQuantities?: Record<string, number>;
  selectedTourIds?: string[];
  onChangeTourQuantity: (tour: Tour, quantity: number) => void | Promise<void>;
  onViewTourInfo?: (tour: Tour) => void;
  onFallbackMessageChange?: (message: string | null) => void;
}

export const ToursCardList: React.FC<ToursCardListProps> = ({
  userLocation,
  radius = 50,
  selectedCityName,
  selectedTourQuantities = {},
  selectedTourIds = [],
  onChangeTourQuantity,
  onViewTourInfo,
  onFallbackMessageChange,
}) => {
  const playBeep = usePlayBeep();
  const vibrate = useVibrate();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
  const [pendingTourIds, setPendingTourIds] = useState<string[]>([]);
  const [pendingTourActions, setPendingTourActions] = useState<Record<string, 'adding' | 'removing'>>({});

  const normalizeText = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        setError(null);
        setFallbackMessage(null);
        onFallbackMessageChange?.(null);

        const { data: products, error: productsError } = await supabase
          .from('products')
          .select(
            'id, title, description, fixed_sale_price, latitude, longitude, departure_time, timezone, main_image_url, image_urls, details, store_id, is_digital'
          )
          .eq('product_type', 'tours')
          .in('status', ['active', 'available'])
          .not('store_id', 'is', null);

        if (productsError) throw productsError;

        const productRows = (products || []) as ProductTourRow[];
        if (productRows.length === 0) {
          setTours([]);
          return;
        }

        const storeIds = Array.from(
          new Set(
            productRows
              .map((p) => p.store_id)
              .filter((storeId): storeId is string => Boolean(storeId))
          )
        );

        if (storeIds.length === 0) {
          setTours([]);
          return;
        }

        const [{ data: stores, error: storesError }, { data: storeAddresses, error: addressesError }] = await Promise.all([
          supabase
            .from('stores')
            .select('id, name, latitude, longitude, status')
            .in('id', storeIds)
            .eq('status', 'active'),
          supabase
            .from('store_addresses')
            .select('store_id, latitude, longitude, is_primary')
            .in('store_id', storeIds)
            .not('latitude', 'is', null)
            .not('longitude', 'is', null),
        ]);

        if (storesError) throw storesError;
        if (addressesError) throw addressesError;

        const storeMap = new Map<string, StoreRow>();
        for (const store of (stores || []) as StoreRow[]) {
          storeMap.set(store.id, store);
        }

        const addressMap = new Map<string, StoreAddressRow>();
        for (const address of (storeAddresses || []) as StoreAddressRow[]) {
          const current = addressMap.get(address.store_id);
          if (!current || (!current.is_primary && address.is_primary)) {
            addressMap.set(address.store_id, address);
          }
        }

        const buildToursForCenter = (centerLat: number, centerLng: number): Tour[] =>
          productRows
            .map((product) => {
              const storeId = product.store_id;
              if (!storeId) return null;

              const store = storeMap.get(storeId);
              if (!store) return null;

              const primaryAddress = addressMap.get(storeId);
              const storeLatitude = primaryAddress?.latitude ?? store.latitude;
              const storeLongitude = primaryAddress?.longitude ?? store.longitude;

              if (!Number.isFinite(storeLatitude) || !Number.isFinite(storeLongitude)) return null;

              const distanceKm = haversineKm(
                centerLat,
                centerLng,
                Number(storeLatitude),
                Number(storeLongitude)
              );

              if (distanceKm > radius) return null;

              return {
                id: product.id,
                title: product.title,
                description: product.description,
                fixed_sale_price: product.fixed_sale_price,
                latitude: product.latitude,
                longitude: product.longitude,
                departure_time: product.departure_time,
                timezone: product.timezone,
                main_image_url: product.main_image_url,
                image_urls: product.image_urls,
                details: product.details,
                distance_km: Number(distanceKm.toFixed(2)),
                store_id: storeId,
                store_name: store.name,
                is_digital: product.is_digital,
              } as Tour;
            })
            .filter((tour): tour is Tour => Boolean(tour))
            .sort((a, b) => (a.distance_km || 0) - (b.distance_km || 0));

        const filteredTours = buildToursForCenter(userLocation.latitude, userLocation.longitude);

        const isSelectedCitySalta = normalizeText(selectedCityName || '') === normalizeText('Salta');

        if (filteredTours.length === 0 && !isSelectedCitySalta) {
          const saltaFallbackTours = buildToursForCenter(
            DEFAULT_LOCATIONS.SALTA.latitude,
            DEFAULT_LOCATIONS.SALTA.longitude
          );

          if (saltaFallbackTours.length > 0) {
            const fallbackMsg = `Sin actividades en ${selectedCityName || 'este lugar'}. Mostrando resultados en Salta.`;
            setFallbackMessage(fallbackMsg);
            onFallbackMessageChange?.(fallbackMsg);
            setTours(saltaFallbackTours);
            return;
          }
        }

        onFallbackMessageChange?.(null);
        setTours(filteredTours);
      } catch (err) {
        console.error('❌ [ToursCardList] Error fetching tours:', err);
        setError('Failed to load tours');
        onFallbackMessageChange?.(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [userLocation.latitude, userLocation.longitude, radius, selectedCityName, onFallbackMessageChange]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Cargando excursiones...</div>;
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (tours.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No hay excursiones disponibles en tu zona</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3 pr-1">
      {fallbackMessage && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{fallbackMessage}</AlertDescription>
        </Alert>
      )}
      {tours.map((tour) => {
        const isSelected = selectedTourIds.includes(tour.id);
        const isPending = pendingTourIds.includes(tour.id);
        const pendingAction = pendingTourActions[tour.id];
        const quantity = selectedTourQuantities[tour.id] || 0;
        const showQuantitySelector = quantity > 0 || isPending;
        
        return (
          <Card 
            key={tour.id} 
            className={cn(
              "hover:border-primary/50 transition cursor-pointer overflow-hidden border-2 relative",
              isSelected ? "border-primary bg-primary/5 shadow-md shadow-primary/10" : "border-transparent"
            )}
            onClick={() => onViewTourInfo?.(tour)}
          >
            {isSelected && (
              <div className="absolute top-2 left-2 z-[5] bg-primary text-white p-1 rounded-full shadow-lg border-2 border-white">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {tour.main_image_url && (
            <div className="w-full h-32 relative overflow-hidden">
              <img 
                src={tour.main_image_url} 
                alt={tour.title}
                className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
              />
              {tour.departure_time && (
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="bg-black/60 text-white backdrop-blur-sm border-none px-2 py-1">
                    <span className="flex flex-col items-center leading-tight">
                      <span className="text-[9px] uppercase tracking-wide text-white/85">Próxima salida:</span>
                      <span className="font-semibold">{formatTimeToAMPM(tour.departure_time)}</span>
                    </span>
                  </Badge>
                </div>
              )}
            </div>
          )}
          <CardHeader className="pb-2 pt-3">
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 group">
                  <h3 className="font-bold text-sm truncate group-hover:text-primary transition-colors">
                    {tour.title}
                  </h3>
                  <div className="text-[10px] text-muted-foreground bg-muted px-1 rounded flex items-center gap-0.5 ml-1">
                    <span>Ver más</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                  {cleanHTMLTags(tour.description)}
                </p>
              </div>
              {!tour.main_image_url && tour.departure_time && (
                <Badge variant="secondary" className="whitespace-nowrap flex-shrink-0 px-2 py-1">
                  <span className="flex flex-col items-center leading-tight">
                    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Próxima salida:</span>
                    <span className="font-semibold">{formatTimeToAMPM(tour.departure_time)}</span>
                  </span>
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pb-3 pt-0 space-y-3">
            <div className="space-y-1 text-[11px]">
              {tour.details?.origin?.name && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground truncate">Desde: {tour.details.origin.name}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Por persona</div>
                <div className="font-bold text-base text-primary">ARS ${tour.fixed_sale_price}</div>
              </div>
              <div className="relative h-[43px] flex items-center justify-end flex-shrink-0 gap-2" onClick={e => e.stopPropagation()}>
                {quantity > 0 ? (
                  <QuantitySelector
                    cartItemId={tour.id}
                    quantity={quantity}
                    onUpdateQuantity={(_cartItemId, quantityCalculator) => {
                      if (isPending) return;
                      const nextQuantity = Math.max(0, quantityCalculator(quantity));
                      setPendingTourIds((prev) => (prev.includes(tour.id) ? prev : [...prev, tour.id]));
                      setPendingTourActions((prev) => ({ ...prev, [tour.id]: nextQuantity > quantity ? 'adding' : 'removing' }));
                      void Promise.resolve(onChangeTourQuantity(tour, nextQuantity)).finally(() => {
                        setPendingTourIds((prev) => prev.filter((id) => id !== tour.id));
                        setPendingTourActions((prev) => {
                          const next = { ...prev };
                          delete next[tour.id];
                          return next;
                        });
                      });
                    }}
                    onPlayBeep={playBeep}
                    onVibrate={vibrate}
                    disabled={isPending}
                  />
                ) : (
                  <Button
                    onClick={async () => {
                      if (isPending) return;
                      vibrate('add');
                      playBeep('add');
                      setPendingTourIds((prev) => (prev.includes(tour.id) ? prev : [...prev, tour.id]));
                      setPendingTourActions((prev) => ({ ...prev, [tour.id]: 'adding' }));
                      try {
                        await Promise.resolve(onChangeTourQuantity(tour, 1));
                      } finally {
                        setPendingTourIds((prev) => prev.filter((id) => id !== tour.id));
                        setPendingTourActions((prev) => {
                          const next = { ...prev };
                          delete next[tour.id];
                          return next;
                        });
                      }
                    }}
                    disabled={isPending}
                    className="h-[43px] px-4 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95 duration-75 flex items-center justify-center gap-2 shrink-0"
                  >
                    <Plus className="h-5 w-5 shrink-0" />
                    <span className="text-sm font-semibold whitespace-nowrap">Agregar</span>
                  </Button>
                )}
                {isPending && (
                  <span className="absolute left-0 -top-6 inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-primary/10 text-primary border border-primary/20 animate-pulse whitespace-nowrap z-10">
                    {pendingAction === 'removing' ? 'Removiendo lugar reservado' : 'Reservando lugar...'}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};
